/*
 * Find a Distributor — filtering, sorting and paging for sections/sumitomo-dealer-locator.
 *
 * Two rules from dealers/DEALER-SEARCH-SPEC.md that the plain design does not show:
 *   - tier 1 = same city as the signed-in buyer, tier 2 = same state, and that ordering is
 *     the default ("Nearest you") rather than an alphabetical dump;
 *   - matching is exact and case-insensitive on values normalised at import — the
 *     storefront must never fuzzy-match, which is why synonyms are resolved on the way in.
 *
 * Icons are inline SVG: the theme's Material Symbols woff2 is a subset and silently
 * renders missing glyphs as their ligature text.
 */

const DealerIcons = {
  storefront: 'M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z',
  location_on: 'M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z',
  call: 'M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.57a1 1 0 01-.25 1.02l-2.2 2.2z',
  mail: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
  arrow_forward: 'M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z',
};

const dealerIcon = (name, size = 16) =>
  `<svg class="sd-svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${DealerIcons[name]}"/></svg>`;

const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

if (!customElements.get('sumitomo-dealer-locator')) {
  customElements.define(
    'sumitomo-dealer-locator',
    class extends HTMLElement {
      connectedCallback() {
        this.perPage = parseInt(this.dataset.perPage, 10) || 9;
        this.page = 1;
        this.buyer = { city: '', state: '', pin: '' };

        // The directory is NOT in the page: it is gated behind our session and fetched
        // from the middleware once the visitor is known (see loadDealers).
        this.dealers = [];
        this.labels = this.readJson('[data-dealer-labels]', {});

        this.stateSel = this.querySelector('[data-filter-state]');
        this.citySel = this.querySelector('[data-filter-city]');
        this.pinInput = this.querySelector('[data-filter-pin]');
        this.sortSel = this.querySelector('[data-sort]');
        this.grid = this.querySelector('[data-dealer-grid]');
        this.empty = this.querySelector('[data-no-results]');
        this.countEl = this.querySelector('[data-result-count]');
        this.pager = this.querySelector('[data-pagination]');

        this.fillStates();
        this.fillCities('');

        this.stateSel?.addEventListener('change', () => {
          this.fillCities(this.stateSel.value);
          this.citySel.value = '';
          this.apply();
        });
        this.citySel?.addEventListener('change', () => this.apply());
        this.sortSel?.addEventListener('change', () => this.apply());
        this.querySelector('[data-search]')?.addEventListener('click', () => this.apply());
        this.querySelector('[data-reset]')?.addEventListener('click', () => this.reset());
        // Live filtering on PIN, as the design does: react once it is meaningful.
        this.pinInput?.addEventListener('input', () => {
          const v = this.pinInput.value;
          if (v.length === 0 || v.length >= 3) this.apply();
        });

        // The buyer's own city/state seeds the default "nearest you" ordering.
        document.addEventListener('sumitomo:session', (e) => this.useSession(e.detail));
        if (window.SumitomoSession?.loaded) this.useSession(window.SumitomoSession);

        // No initial render: the list arrives with the session. Rendering now would flash
        // "no dealer found" at a buyer who is simply still being identified.
      }

      readJson(selector, fallback) {
        const el = this.querySelector(selector);
        if (!el) return fallback;
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          console.error('[dealer-locator] bad JSON in', selector, e);
          this.dataBroken = true;
          return fallback;
        }
      }

      /** Metaobject list fields arrive as a JSON array string. */
      parseTags(value) {
        if (Array.isArray(value)) return value;
        try {
          const parsed = JSON.parse(value || '[]');
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return String(value || '')
            .split(/[|;,]/)
            .map((t) => t.trim())
            .filter(Boolean);
        }
      }

      /** GET /dealers — signed-in only. A failure here is OUR fault, so it shows the
       *  "directory failed to load" state rather than an empty, blameless list. */
      async loadDealers(base) {
        if (this.dealersLoaded) return;
        this.dealersLoaded = true;
        try {
          const res = await fetch(`${base}/dealers`, { credentials: 'include', headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          this.dealers = (data.dealers || []).map((d) => ({
            ...d,
            tags: Array.isArray(d.tags) ? d.tags : this.parseTags(d.tags),
            territories: Array.isArray(d.territories) ? d.territories : [],
          }));
          this.dataBroken = false;
        } catch (e) {
          console.error('[dealer-locator] could not load the directory', e);
          this.dealers = [];
          this.dataBroken = true;
          this.dealersLoaded = false; // a later session event may retry
        }
        this.fillStates();
        this.fillCities('');
        this.apply();
      }

      async useSession(state) {
        if (!state || state.tier === 'guest') return;
        const base = (window.SUMITOMO_API_BASE || '').replace(/\/$/, '');
        if (!base) return;

        // Any signed-in buyer may see the directory; only a verified one has a profile
        // address worth ordering it by.
        await this.loadDealers(base);
        if (state.tier !== 'verified') return;

        // The address lives on the profile, not the session payload, so ask for it once.
        if (this.buyerLoaded) return;
        this.buyerLoaded = true;
        try {
          const res = await fetch(`${base}/account/profile`, { credentials: 'include', headers: { Accept: 'application/json' } });
          if (!res.ok) return;
          const profile = await res.json();
          this.buyer = {
            city: (profile.address?.city || '').trim(),
            state: (profile.address?.province || profile.address?.provinceCode || '').trim(),
            pin: (profile.address?.zip || '').replace(/\D/g, ''),
          };
          this.apply();
        } catch (e) {
          /* directory still works, just without the personalised order */
        }
      }

      fillStates() {
        if (!this.stateSel) return;
        // Runs again after the directory arrives — keep only the "All states" option.
        const keep = this.stateSel.options[0];
        this.stateSel.innerHTML = '';
        this.stateSel.add(keep);
        [...new Set(this.dealers.map((d) => d.state).filter(Boolean))].sort().forEach((state) => {
          this.stateSel.add(new Option(state, state));
        });
      }

      fillCities(state) {
        if (!this.citySel) return;
        const keep = this.citySel.options[0];
        this.citySel.innerHTML = '';
        this.citySel.add(keep);
        [...new Set(this.dealers.filter((d) => !state || d.state === state).map((d) => d.city).filter(Boolean))]
          .sort()
          .forEach((city) => this.citySel.add(new Option(city, city)));
      }

      /** Exact, case-insensitive, on values already normalised at import. */
      matches(dealer) {
        const state = this.stateSel?.value || '';
        const city = this.citySel?.value || '';
        const pin = (this.pinInput?.value || '').trim();

        if (state && dealer.state !== state) return false;
        if (city) {
          const covers = [dealer.city, ...(dealer.territories || [])].map((c) => c.toLowerCase());
          if (!covers.includes(city.toLowerCase())) return false;
        }
        if (pin && !(dealer.pin || '').startsWith(pin)) return false;
        return true;
      }

      /**
       * Proximity rank, lower is nearer.
       *
       * PIN leads, city follows. Exact city matching alone put a Pune buyer below
       * Aurangabad and Mumbai: Shopify stores their city as "Pune City East" while the
       * dealer master says "Pune", so tier 1 never hit and everything fell to
       * same-state ordered alphabetically. City strings come from address autocomplete
       * and will not reliably equal a hand-maintained dealer master.
       *
       * Indian PINs are hierarchical — the first 3 digits are the sorting district
       * (411 = Pune, 400/401 = Mumbai, 431 = Aurangabad) — so a prefix comparison is
       * real proximity, and it is exact arithmetic rather than the fuzzy string
       * matching DEALER-SEARCH-SPEC.md rules out.
       */
      tierOf(dealer) {
        const city = this.buyer.city.toLowerCase();
        const state = this.buyer.state.toLowerCase();
        const pin = this.buyer.pin;
        const dealerPin = (dealer.pin || '').replace(/\D/g, '');

        if (pin && dealerPin) {
          if (pin === dealerPin) return 0;                                  // same postal code
          if (pin.slice(0, 3) === dealerPin.slice(0, 3)) return 1;          // same postal district
        }
        if (city && [dealer.city, ...(dealer.territories || [])].some((c) => c.toLowerCase() === city)) return 1;
        if (pin && dealerPin && pin.slice(0, 2) === dealerPin.slice(0, 2)) return 2; // neighbouring districts
        if (state && dealer.state.toLowerCase() === state) return 3;
        return 4;
      }

      /** Within a tier, the closer PIN wins — so the order reads as "nearest" rather
       *  than alphabetical. Falls back to 0 when either side has no usable PIN. */
      pinGap(dealer) {
        const a = parseInt(this.buyer.pin, 10);
        const b = parseInt((dealer.pin || '').replace(/\D/g, ''), 10);
        return Number.isFinite(a) && Number.isFinite(b) ? Math.abs(a - b) : 0;
      }

      sorted(list) {
        const key = this.sortSel?.value || 'nearest';
        const byText = (a, b, field) => (a[field] || '').localeCompare(b[field] || '');
        if (key === 'nearest') {
          return [...list].sort(
            (a, b) =>
              this.tierOf(a) - this.tierOf(b) ||
              this.pinGap(a) - this.pinGap(b) ||
              byText(a, b, 'city') ||
              byText(a, b, 'name'),
          );
        }
        return [...list].sort((a, b) => byText(a, b, key) || byText(a, b, 'name'));
      }

      apply() {
        this.filtered = this.sorted(this.dealers.filter((d) => this.matches(d)));
        this.page = 1;
        this.render();
      }

      reset() {
        if (this.stateSel) this.stateSel.value = '';
        if (this.pinInput) this.pinInput.value = '';
        this.fillCities('');
        if (this.citySel) this.citySel.value = '';
        if (this.sortSel) this.sortSel.value = 'nearest';
        this.apply();
      }

      render() {
        const total = this.filtered.length;
        const start = (this.page - 1) * this.perPage;
        const pageItems = this.filtered.slice(start, start + this.perPage);

        if (this.grid) this.grid.innerHTML = pageItems.map((d) => this.card(d)).join('');
        if (this.empty) {
          this.empty.hidden = total > 0;
          // Distinguish "no dealer matches your filters" from "the directory failed to
          // load" and from "no dealers have been published yet" — three different fixes.
          const title = this.empty.querySelector('.sd-empty-title');
          const text = this.empty.querySelector('.sd-empty-text');
          if (title && text) {
            if (this.dataBroken) {
              title.textContent = this.dataset.errBrokenTitle || title.textContent;
              text.textContent = this.dataset.errBrokenText || text.textContent;
            } else if (!this.dealers.length) {
              title.textContent = this.dataset.errEmptyTitle || title.textContent;
              text.textContent = this.dataset.errEmptyText || text.textContent;
            }
          }
        }
        if (this.grid) this.grid.hidden = total === 0;

        if (this.countEl) {
          const filtering = Boolean(this.stateSel?.value || this.citySel?.value || this.pinInput?.value);
          // Only claim "nearest you" when a buyer location actually seeded the order.
          const nearest = !filtering && (this.buyer.pin || this.buyer.city) && this.sortSel?.value === 'nearest';
          const template = nearest
            ? this.labels.nearYou
            : filtering
              ? this.labels.showingSome
              : this.labels.showingAll;
          this.countEl.textContent = (template || '')
            .replace('{count}', total)
            .replace('{city}', this.buyer.city);
        }

        this.renderPager(total);
      }

      card(d) {
        const tagClass = (tag) => {
          const t = tag.toLowerCase();
          if (t.includes('authoris') || t.includes('authoriz')) return 'sd-tag-authorised';
          if (t.includes('range')) return 'sd-tag-range';
          return 'sd-tag-tech';
        };
        const tags = (d.tags || []).map((t) => `<span class="sd-tag ${tagClass(t)}">${esc(t)}</span>`).join('');
        const place = [d.city, d.state].filter(Boolean).join(', ');
        const where = [d.address, d.pin ? `PIN ${d.pin}` : ''].filter(Boolean).join(' · ');

        return `<div class="sd-card">
          <div class="sd-card-head">
            <div>
              <h3 class="sd-card-name">${esc(d.name)}</h3>
              <p class="sd-card-place">${esc(place)}</p>
            </div>
            <span class="sd-card-icon">${dealerIcon('storefront', 24)}</span>
          </div>
          ${tags ? `<div class="sd-tags">${tags}</div>` : ''}
          <div class="sd-card-lines">
            ${where ? `<div class="sd-line">${dealerIcon('location_on')}<span>${esc(where)}</span></div>` : ''}
            ${d.phone ? `<div class="sd-line">${dealerIcon('call')}<a href="tel:${esc((d.phone || '').replace(/[^\d+]/g, ''))}">${esc(d.phone)}</a></div>` : ''}
            ${d.email ? `<div class="sd-line">${dealerIcon('mail')}<a href="mailto:${esc(d.email)}">${esc(d.email)}</a></div>` : ''}
          </div>
          <!-- Send enquiry removed (SHIN feedback 31 Aug, slide 8): each card already
               shows the dealer's own email and phone, so the buyer contacts them directly. -->
        </div>`;
      }

      renderPager(total) {
        if (!this.pager) return;
        const pages = Math.ceil(total / this.perPage);
        if (pages <= 1) {
          this.pager.innerHTML = '';
          return;
        }
        const button = (label, page, opts = {}) =>
          `<button type="button" class="sd-page-btn${opts.active ? ' is-active' : ''}" data-page="${page}"${opts.disabled ? ' disabled' : ''}>${esc(label)}</button>`;

        let html = button(this.labels.prev || 'Prev', this.page - 1, { disabled: this.page === 1 });
        for (let i = 1; i <= pages; i++) html += button(String(i), i, { active: i === this.page });
        html += button(this.labels.next || 'Next', this.page + 1, { disabled: this.page === pages });
        this.pager.innerHTML = html;

        this.pager.querySelectorAll('[data-page]').forEach((btn) =>
          btn.addEventListener('click', () => {
            this.page = parseInt(btn.dataset.page, 10);
            this.render();
            this.querySelector('.sd-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }),
        );
      }
    },
  );
}
